// PWA install helpers.
//
// `beforeinstallprompt` fires once per page load, often before the component
// that wants it mounts (e.g. on /order, while the prompt lives on
// /confirmation). This module is imported from main.jsx so the event is
// captured at startup and kept for whoever asks later.

const PROMPTED_AT_KEY = 'pwa_install_prompted_at'
const RESULT_KEY = 'pwa_install_result'

// How long to wait before offering again, per outcome.
//
//   dismissed  → they said no. 14 days.
//   shown_ios  → they TAPPED INSTALL and we showed them the steps. We cannot
//                know whether it worked, so 3 days: a failed attempt used to
//                be recorded as a refusal and buried the banner for two
//                weeks — that is exactly how a customer who tried and failed
//                was never offered it again.
//   accepted   → never.
const REPROMPT_DAYS = { dismissed: 14, shown_ios: 3 }
const DEFAULT_REPROMPT_DAYS = 14

let deferredEvent = null
const listeners = new Set()
const notify = () => listeners.forEach((fn) => fn())

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferredEvent = e
    notify()
  })
  window.addEventListener('appinstalled', () => {
    deferredEvent = null
    store(RESULT_KEY, 'accepted')
    notify()
  })
}

// iOS never fires `appinstalled`. The only way to learn that someone finally
// succeeded is to notice we are running standalone — so record it the first
// time the app opens from the home screen, and stop asking forever after.
if (typeof window !== 'undefined') {
  queueMicrotask(() => {
    try {
      const standalone =
        window.matchMedia?.('(display-mode: standalone)').matches ||
        window.navigator.standalone === true
      if (standalone) store(RESULT_KEY, 'accepted')
    } catch { /* private mode */ }
  })
}

function store(key, value) {
  try { localStorage.setItem(key, value) } catch { /* private mode */ }
}
function read(key) {
  try { return localStorage.getItem(key) } catch { return null }
}

export function subscribe(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export function isStandalone() {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  )
}

export function isIos() {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  // iPadOS 13+ reports as a Mac — tell it apart by touch support.
  const iPadOs = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1
  return (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) || iPadOs
}

export function canPromptNative() {
  return !!deferredEvent
}

// Instagram, Facebook, WhatsApp, Gmail, TikTok… open links in their OWN
// embedded browser. On iOS those report as Safari — isIos() is true — but
// their Share sheet has no "Add to Home Screen". So the install banner used
// to appear, the customer tapped Install, and the instructions told them to
// use a button that does not exist in that app. It was impossible to follow.
//
// There is nothing to offer here. The only honest move is to send them to a
// real browser.
export function isInAppBrowser() {
  if (typeof navigator === 'undefined') return false
  return /FBAN|FBAV|FB_IAB|Instagram|Line\/|WhatsApp|GSA\/|TikTok|Snapchat|Twitter|LinkedInApp/i
    .test(navigator.userAgent)
}

// The browser we tell them to open instead.
export function realBrowserName() {
  return isIos() ? 'Safari' : 'Chrome'
}

// Can we offer *something* that actually works — the native prompt or the
// iOS steps? False inside an in-app browser: see isInAppBrowser().
export function canOfferInstall() {
  if (isStandalone() || isInAppBrowser()) return false
  return canPromptNative() || isIos()
}

// Auto-prompt gate: not installed, something to offer, and the re-prompt
// delay for the last outcome has passed. An accepted install never returns.
export function shouldAutoPrompt() {
  if (isStandalone()) return false
  // In an in-app browser we still show a banner, but a different one — it
  // explains how to get to a real browser instead of offering an install.
  if (!canOfferInstall() && !isInAppBrowser()) return false
  const result = read(RESULT_KEY)
  if (result === 'accepted') return false
  const at = Number(read(PROMPTED_AT_KEY)) || 0
  if (!at) return true
  const days = REPROMPT_DAYS[result] ?? DEFAULT_REPROMPT_DAYS
  return Date.now() - at > days * 24 * 3600 * 1000
}

export function markPrompted(result) {
  store(PROMPTED_AT_KEY, String(Date.now()))
  if (result) store(RESULT_KEY, result)
}

// Fire the native Android/Chrome prompt. Resolves to 'accepted' | 'dismissed'
// | 'unavailable'. The event is single-use, so it's cleared afterwards.
export async function promptNativeInstall() {
  if (!deferredEvent) return 'unavailable'
  const e = deferredEvent
  deferredEvent = null
  notify()
  e.prompt()
  const choice = await e.userChoice.catch(() => null)
  const outcome = choice?.outcome === 'accepted' ? 'accepted' : 'dismissed'
  markPrompted(outcome)
  return outcome
}
